import Image from 'next/image';

import { BodyLarge, BodyRegular } from '@common/body';
import { H3, H4, H5 } from '@common/headline';
import { TakeOurQuizButton } from '../Hero/TakeOurQuizCTA';

import mounjaroPen from '@public/img/medically-supported/mounjaro-pen.png';
import yoga from '@public/img/medically-supported/yoga.png';
import weightLossPercentagePill from '@public/img/how-it-works/weight-loss-percentage-pill.png';

const subtitle = (
  <>
    Clinically-proven Mounjaro medication helps to jumpstart your weight loss;
    Second Nature{"'"}s programme keeps it off for good.
  </>
);

type Props = {
  utmCampaign?: string;
  utmMedium?: string;
  utmSource?: string;
};

export const MounjaroLifelongResults = ({
  utmCampaign,
  utmMedium,
  utmSource,
}: Props): JSX.Element => (
  <div className="flex w-full items-center justify-center bg-blue pb-40 pt-20 lg:px-[60px] lg:py-24">
    <div className="flex max-w-[351px] flex-col items-center justify-between lg:w-[760px] lg:max-w-none xl:w-[1320px] xl:flex-row">
      <div className="text-center text-mono-white xl:max-w-[448px] xl:text-start">
        <H3>
          Our powerful combined approach{' '}
          <span className="text-teal">for lifelong results</span>
        </H3>
        <div className="mt-4 hidden text-blue-200 lg:block">
          <BodyLarge>{subtitle}</BodyLarge>
        </div>
        <div className="mt-2 text-blue-200 lg:hidden">
          <BodyRegular>{subtitle}</BodyRegular>
        </div>
        <div className="mt-12 hidden xl:block">
          <TakeOurQuizButton
            colour="Teal"
            ctaText="Check my eligibility"
            isGlp1
            location="Mounjaro lifelong results"
            type="Primary"
            utmCampaign={utmCampaign}
            utmMedium={utmMedium}
            utmSource={utmSource}
          />
        </div>
      </div>
      <div className="mt-12 hidden flex-col gap-6 lg:flex xl:mt-0">
        <div className="flex gap-6">
          <div className="relative flex-none overflow-hidden rounded-3xl">
            <Image
              src={mounjaroPen}
              height={200}
              width={200}
              alt="Medication box"
            />
          </div>
          <div className="mt-2 max-w-[424px] text-mono-white">
            <Image
              src={weightLossPercentagePill}
              alt="Lose up to 20-25% weight-loss"
              width={223}
              height={36}
            />
            <div className="mt-1">
              <H5>Mounjaro, a ground-breaking medication</H5>
            </div>
            <div className="mt-2 text-blue-200">
              <BodyRegular>
                Our weight-loss medication is prescribed by a pharmacist.
                <br />
                <br />
                It{"'"}s proven to help you lose weight by mimicking the hormone
                responsible for curbing appetite. As such, it reduces hunger and
                keeps you feeling fuller for longer.
              </BodyRegular>
            </div>
          </div>
        </div>
        <div className="flex gap-6">
          <div className="relative flex-none overflow-hidden rounded-3xl">
            <Image src={yoga} width={200} height={200} alt="Medication box" />
          </div>
          <div className="max-w-[424px] text-mono-white">
            <div className="mt-1">
              <H5>An award-winning, habit-change programme</H5>
              <div className="mt-2 text-blue-200">
                <BodyRegular>
                  Research shows once you stop taking weight-loss medication,
                  weight regain is likely, as your body becomes dependent on the
                  medication to manage your appetite.
                  <br />
                  <br />
                  That{"'"}s where Second Nature comes in. We help you to
                  establish lifelong healthy habits while taking the medication.
                  This way, you can maintain your weight loss in the long-run,
                  even after stopping the medication.
                </BodyRegular>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-12 flex flex-col gap-12 lg:hidden">
        <div className="flex max-w-[351px] flex-col items-center text-left">
          <div className="relative overflow-hidden rounded-3xl">
            <Image
              src={mounjaroPen}
              alt="Medication box"
              height={200}
              width={200}
            />
          </div>
          <div className="mt-8">
            <Image
              src={weightLossPercentagePill}
              alt="Lose up to 20-25% weight-loss"
              width={223}
              height={36}
            />
          </div>
          <div className="mt-2 w-full text-mono-white">
            <H4>Mounjaro, a ground-breaking medication</H4>
          </div>
          <div className="mt-2 w-full text-blue-200">
            <BodyRegular>
              Our weight-loss medication is prescribed by a pharmacist.
              <br />
              <br />
              It{"'"}s proven to help you lose weight by mimicking the hormone
              responsible for curbing appetite. As such, it reduces hunger and
              keeps you feeling fuller for longer.
            </BodyRegular>
          </div>
        </div>
        <div className="flex max-w-[351px] flex-col items-center text-left">
          <div className="relative overflow-hidden rounded-3xl">
            <Image src={yoga} alt="Medication box" width={200} height={200} />
          </div>
          <div className="mt-8 w-full text-mono-white">
            <H4>An award-winning, habit-change programme</H4>
          </div>
          <div className="mt-2 w-full text-blue-200">
            <BodyRegular>
              Research shows once you stop taking weight-loss medication, weight
              regain is likely, as your body becomes dependent on the medication
              to manage your appetite.
              <br />
              <br />
              That{"'"}s where Second Nature comes in. We help you to establish
              lifelong healthy habits while taking the medication. This way, you
              can maintain your weight loss in the long-run, even after stopping
              the medication.
            </BodyRegular>
          </div>
        </div>
      </div>
      <div className="mt-[52px] flex min-w-full justify-center xl:hidden">
        <TakeOurQuizButton
          colour="Teal"
          ctaText="Check my eligibility"
          fullWidth
          isGlp1
          location="Mounjaro lifelong results"
          type="Primary"
          utmCampaign={utmCampaign}
          utmMedium={utmMedium}
          utmSource={utmSource}
        />
      </div>
    </div>
  </div>
);
